import React, { useState } from "react";
import { Dialog } from "components/dialog";
import { subscriptionPlansApi } from "api/models";
import { handleAxiosAndCaughtError, validateJson } from "utils";
import toast from "react-hot-toast";
import { Plus } from "lucide-react";
import PureDropDown, { DropdownOption } from "components/pure-dropdown";
import { useTranslation } from "react-i18next";

interface CreateSubscriptionPlanDialogProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
  frequencyOptions: DropdownOption[];
}

const CreateSubscriptionPlanDialog: React.FC<CreateSubscriptionPlanDialogProps> = ({
  isOpen,
  onClose,
  onSuccess,
  frequencyOptions,
}) => {
  const { t } = useTranslation();
  const [isCreating, setIsCreating] = useState(false);
  const [createForm, setCreateForm] = useState({
    name: "",
    price: 0,
    frequency: "" as string,
    configurations: {
      numOfWorkspaces: 0,
      numOfUsers: 0,
      numOfAgents: 0,
      numOfWorkflows: 0,
      maxStorageSize: 0
    },
    is_enterprise: false,
  });

  const [createFormText, setCreateFormText] = useState({
    configurations: JSON.stringify({
      numOfWorkspaces: 0,
      numOfUsers: 0,
      numOfAgents: 0,
      numOfWorkflows: 0,
      maxStorageSize: 0
    }, null, 2),
  });

  const [jsonErrors, setJsonErrors] = useState({
    configurations: "",
  });

  // Options are provided by parent

  const handleJsonChange = (field: 'configurations', value: string) => {
    setCreateFormText(prev => ({ ...prev, [field]: value }));
    setJsonErrors(prev => ({ ...prev, [field]: validateJson(value) }));
  };

  const handleFrequencyChange = (value: string | string[]) => {
    const frequencyValue = Array.isArray(value) ? value[0] : value;
    setCreateForm(prev => ({ ...prev, frequency: frequencyValue || "" }));
  };


  const handleCreateSave = async () => {
    if (!createForm.name.trim()) {
      toast.error(t("subscriptionPlans.dialog.create.messages.nameRequired"));
      return;
    }

    if (createForm.price < 0) {
      toast.error(t("subscriptionPlans.dialog.create.messages.priceInvalid"));
      return;
    }

    // Check for JSON validation errors
    if (jsonErrors.configurations) {
      toast.error(t("subscriptionPlans.dialog.create.messages.fixJsonErrors"));
      return;
    }

    // Parse JSON text to objects
    let parsedConfigurations = {};
    
    try {
      parsedConfigurations = JSON.parse(createFormText.configurations);
    } catch (e) {
      toast.error(t("subscriptionPlans.dialog.create.messages.invalidJson"));
      return;
    }

    setIsCreating(true);
    try {
      const formData = {
        name: createForm.name,
        price: createForm.price,
        frequency: createForm.frequency,
        configurations: parsedConfigurations,
        is_enterprise: !!createForm.is_enterprise,
      };
      await subscriptionPlansApi.create(formData);
      await onSuccess();
      toast.success(t("subscriptionPlans.dialog.create.messages.createdSuccessfully"));
      onClose();
    } catch (err) {
      handleAxiosAndCaughtError(err);
    } finally {
      setIsCreating(false);
    }
  };

  const handleClose = () => {
    // Reset form when closing
    const defaultConfigurations = {
      numOfWorkspaces: 0,
      numOfUsers: 0,
      numOfAgents: 0,
      numOfWorkflows: 0,
      maxStorageSize: 0
    };
    
    setCreateForm({
      name: "",
      price: 0,
      frequency: "",
      configurations: defaultConfigurations,
      is_enterprise: false,
    });
    setCreateFormText({
      configurations: JSON.stringify(defaultConfigurations, null, 2),
    });
    setJsonErrors({
      configurations: "",
    });
    onClose();
  };

  return (
    <Dialog isOpen={isOpen} setIsOpen={handleClose} outsideClickDisabled={true} backdropHandlerDisabled={true}>
      <div className="flex min-h-[60vh] w-full max-w-[700px] flex-col gap-4 sm:gap-6">
        <div className="border-b border-gray-200 pb-3 sm:pb-4">
          <div className="flex items-center gap-2">
            <Plus className="h-5 w-5 text-blue-500" />
            <h2 className="text-lg font-bold text-gray-900 dark:text-white sm:text-xl">
              {t("subscriptionPlans.dialog.create.title")}
            </h2>
          </div>
          <p className="mt-1 text-xs text-gray-500 dark:text-gray-400 sm:text-sm">
            {t("subscriptionPlans.dialog.create.description")}
          </p>
        </div>

        <div className="space-y-4">
          <div>
            <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
              {t("subscriptionPlans.dialog.create.nameLabel")}
            </label>
            <input
              type="text"
              value={createForm.name}
              onChange={(e) => setCreateForm(prev => ({ ...prev, name: e.target.value }))}
              className="mt-1 w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500 dark:border-gray-600 dark:bg-gray-700 dark:text-white"
              placeholder={t("subscriptionPlans.dialog.create.namePlaceholder")}
            />
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
                {t("subscriptionPlans.dialog.create.priceLabel")}
              </label>
              <input
                type="number"
                min={0}
                step="0.01"
                value={Number.isNaN(createForm.price as number) ? 0 : createForm.price}
                onChange={(e) => setCreateForm(prev => ({ ...prev, price: Number(e.target.value) }))}
                className="mt-1 w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500 dark:border-gray-600 dark:bg-gray-700 dark:text-white"
                placeholder={t("subscriptionPlans.dialog.create.pricePlaceholder")}
              />
            </div>
            <div>
              <PureDropDown
                options={frequencyOptions}
                value={createForm.frequency}
                onChange={handleFrequencyChange}
                placeholder={t("subscriptionPlans.dialog.create.billingFrequencyPlaceholder")}
                label={t("subscriptionPlans.dialog.create.billingFrequencyLabel")}
                showClearButton={false}
                className="mt-1"
              />
            </div>
          </div>

           <div>
             <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
               {t("subscriptionPlans.dialog.create.configurationsLabel")}
             </label>
             <textarea
               value={createFormText.configurations}
               onChange={(e) => handleJsonChange('configurations', e.target.value)}
               className={`mt-1 w-full rounded-lg border px-4 py-3 text-sm font-mono focus:outline-none focus:ring-2 focus:ring-blue-500/20 transition-all duration-200 dark:bg-gray-700 dark:text-white ${
                 jsonErrors.configurations 
                   ? 'border-red-500 focus:border-red-500 focus:ring-red-500/20 bg-red-50 dark:bg-red-900/10' 
                   : 'border-gray-300 focus:border-blue-500 dark:border-gray-600'
               }`}
               placeholder={t("subscriptionPlans.dialog.create.configurationsPlaceholder")}
               rows={8}
             />
             {jsonErrors.configurations && (
               <div className="mt-2 flex items-center gap-2 text-sm text-red-600 dark:text-red-400">
                 <svg className="h-4 w-4 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                   <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                 </svg>
                 {jsonErrors.configurations}
               </div>
             )}
             <div className="mt-2 text-xs text-gray-500 dark:text-gray-400">
               {t("subscriptionPlans.dialog.create.configurationsHint")}
             </div>
           </div>

          <div className="flex items-center gap-3">
            <input
              id="is_enterprise"
              type="checkbox"
              checked={!!createForm.is_enterprise}
              onChange={(e) => setCreateForm(prev => ({ ...prev, is_enterprise: e.target.checked }))}
              className="h-5 w-5 rounded-md border-gray-300 accent-indigo-600 focus:ring-indigo-500"
            />
            <label htmlFor="is_enterprise" className="text-sm font-medium text-gray-700 dark:text-gray-300">
              {t("subscriptionPlans.dialog.create.enterprisePlanLabel")}
            </label>
          </div>
        </div>

        <div className="flex justify-end pt-3 sm:pt-4">
          <button
            type="button"
            onClick={handleCreateSave}
            disabled={isCreating || !createForm.name.trim() || !!jsonErrors.configurations || createForm.price < 0}
            className="flex items-center justify-center gap-2 rounded-lg bg-blue-500 px-6 py-2 font-semibold text-white transition-colors hover:bg-blue-600 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-opacity-20 disabled:cursor-not-allowed disabled:opacity-50"
          >
            {isCreating ? (
              <div className="h-4 w-4 animate-spin rounded-full border-2 border-white border-t-transparent" />
            ) : (
              <Plus size={16} />
            )}
            {t("subscriptionPlans.dialog.create.addPlanButton")}
          </button>
        </div>
      </div>
    </Dialog>
  );
};

export default CreateSubscriptionPlanDialog;

